import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutoSizer } from 'react-virtualized';

import { GridContext } from './context/GridContext';
import { GridHeaders } from './headers/GridHeaders';
import { useGridScrollbars } from './hooks/useGridScrollbars';
import { useGridStructure } from './hooks/useGridStructure';
import { useGridTheme } from './hooks/useGridTheme';
import { useGridWidths } from './hooks/useGridWidths';
import { useVirtualizedGrid } from './hooks/useVirtualizedGrid';
import { GridElement } from './element';
import { GridRows } from './GridRows';
import { GridSummary } from './GridSummary';
import { GridContextProps, GridProps } from './types';
import { calculateDepth } from './utils';

import cn from './Grid.module.scss';

const DataGrid = <T extends Record<string, any>>({
  rows = [],
  summary = [],
  children,
  header_height = 34,
  row_height = 34,
  height = 0,
  width = 0,
  header_clamp = 2,
  overscan = 5,
  theme,
}: GridProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [grid_width, setGridWidth] = useState(0);
  const grid_visibility = width && grid_width ? 'visible' : 'hidden';

  useEffect(() => {
    const updateContainerWidth = () => {
      if (containerRef.current) {
        setGridWidth(containerRef.current.clientWidth);
      }
    };

    // autosizer measures the parent but we also need to measure the container
    // container is 1px border smaller
    updateContainerWidth();

    const resizeObserver = new ResizeObserver(updateContainerWidth);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [containerRef]);

  const {
    rows_sorted,
    headers,
    depth,
    columns,
    column_widths_resized,
    column_filters,
    setColumns,
    resizeColumn,
    filterColumn,
    setColumnSortDirection,
    updateColumnSortDirection,
    moveColumn,
  } = useGridStructure({ children, rows });

  const {
    column_widths,
    cumulative_column_widths,
    cumulative_fixed_left_widths,
    cumulative_fixed_right_widths,
    total_columns_width,
  } = useGridWidths({ headers, columns, column_widths_resized, width: grid_width });

  const { visible_rows, is_scrolling, start_index, total_height, onScroll, velocity } =
    useVirtualizedGrid({
      rows: rows_sorted,
      row_height,
      height,
      overscan,
    });

  const scrollbar_height = useGridScrollbars({ columns_width: total_columns_width, grid_width });

  const filler_width = Math.max(0, Math.round(grid_width) - Math.round(total_columns_width));

  const filler_required = filler_width > 0;

  const grid_theme = useGridTheme(theme);

  const value: GridContextProps<T> = useMemo(
    () => ({
      headers,
      depth,
      columns,
      column_widths,
      cumulative_column_widths,
      cumulative_fixed_left_widths,
      cumulative_fixed_right_widths,
      filler_required,
      filler_width,
      height,
      width,
      column_filters,
      start_index,
      total_columns_width,
      visible_rows,
      summary,
      header_height,
      header_clamp,
      row_height,
      overscan,
      theme: grid_theme,
      is_scrolling,
      velocity,
      setColumns,
      moveColumn,
      resizeColumn,
      filterColumn,
      setColumnSortDirection,
      updateColumnSortDirection,
    }),
    [
      column_widths,
      columns,
      column_filters,
      cumulative_column_widths,
      cumulative_fixed_left_widths,
      overscan,
      cumulative_fixed_right_widths,
      velocity,
      depth,
      filler_required,
      filler_width,
      height,
      width,
      grid_theme,
      header_clamp,
      header_height,
      headers,
      moveColumn,
      resizeColumn,
      row_height,
      setColumns,
      start_index,
      summary,
      total_columns_width,
      filterColumn,
      setColumnSortDirection,
      updateColumnSortDirection,
      visible_rows,
      is_scrolling,
    ]
  );

  return (
    <GridElement
      className={cn.grid}
      theme={grid_theme}
      ref={containerRef}
      style={{
        minHeight: `${header_height * depth + row_height}px`,
        height: `${height + scrollbar_height}px`,
        width: `${width}px`,
        outline: grid_theme.general.outline,
        borderRadius: grid_theme.general.border_radius,
        visibility: grid_visibility,
      }}
      onScroll={onScroll}
    >
      <GridContext.Provider value={value}>
        <div
          className={cn.relative}
          style={{ height: `${total_height}px`, width: `${total_columns_width}px` }}
        >
          <div
            className={cn.headers}
            style={{ width: `${total_columns_width}px`, height: `${depth * header_height}px` }}
          >
            <GridHeaders />
          </div>
          <div
            className={cn.relative}
            style={{ height: `${total_height}px`, width: `${total_columns_width}px` }}
          >
            <GridRows />
          </div>
          {!!summary.length && (
            <div
              className={cn.summary}
              style={{ width: `${total_columns_width}px`, outline: grid_theme.general.outline }}
            >
              <GridSummary />
            </div>
          )}
        </div>
      </GridContext.Provider>
    </GridElement>
  );
};

export const Grid = <T extends Record<string, any>>({
  rows = [],
  summary = [],
  children,
  header_height = 32,
  row_height = 40,
  header_clamp = 1,
  overscan = 5,
  disable_autosizer = false,
  theme,
}: Omit<GridProps<T>, 'height' | 'width'>) => {
  const depth = useMemo(() => calculateDepth(children), [children]);

  const measureGridHeight = useCallback(
    (height: number) => {
      const headers_height = depth * header_height;
      const rows_height = rows.length * row_height;
      const summary_height = summary.length * row_height;
      const measured_height = headers_height + rows_height + summary_height;
      if (disable_autosizer) {
        return measured_height;
      }
      if (measured_height > height) {
        return height;
      }
      return measured_height;
    },
    [depth, header_height, row_height, disable_autosizer, rows.length, summary.length]
  );

  return (
    <AutoSizer disableHeight={disable_autosizer}>
      {({ height, width }) => (
        <DataGrid
          height={measureGridHeight(height)}
          width={width}
          rows={rows}
          summary={summary}
          header_height={header_height}
          header_clamp={header_clamp}
          row_height={row_height}
          overscan={overscan}
          theme={theme}
        >
          {children}
        </DataGrid>
      )}
    </AutoSizer>
  );
};
